import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { normalizeChannelHandle } from '@jwp/ott-common/src/utils/channelHandle';
import { GET_MY_CHANNEL, type GetMyChannelData } from '@jwp/ott-common/src/graphql/query/channel.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import SuccessCircleIcon from '@jwp/ott-theme/assets/icons/success_circle.svg?react';

import Button from '../Button/Button';

import styles from './ChannelCreatedModal.module.scss';

type LocationState = {
  channelHandle?: string;
};

const ChannelCreatedModal = () => {
  const { t } = useTranslation('user');
  const navigate = useNavigate();
  const location = useLocation();
  const locationHandle = normalizeChannelHandle((location.state as LocationState | null)?.channelHandle || '');

  const { data } = useQuery<GetMyChannelData>(GET_MY_CHANNEL, {
    client: getAuthApolloClient(),
    fetchPolicy: 'network-only',
    skip: Boolean(locationHandle),
  });

  const apiHandle = normalizeChannelHandle(data?.getMyChannel?.channels?.[0]?.chHandle || '');
  const channelHandle = locationHandle || apiHandle;

  const onBackHome = () => {
    navigate(createURLFromLocation(location, { u: null }), { replace: true });
  };

  const onViewChannel = () => {
    if (!channelHandle) {
      onBackHome();
      return;
    }
    navigate(`/channel/${channelHandle}`);
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconWrap} aria-hidden="true">
        <SuccessCircleIcon className={styles.successIcon} />
      </div>
      <h2 className={styles.title}>{t('channel_created.title')}</h2>
      <p className={styles.description}>{t('channel_created.description')}</p>
      <div className={styles.actions}>
        <Button
          label={t('channel_created.back_to_home')}
          variant="transparent"
          onClick={onBackHome}
          className={styles.backHome}
        />
        <Button
          label={t('channel_created.view_channel')}
          variant="contained"
          color="primary"
          onClick={onViewChannel}
          className={styles.viewChannel}
        />
      </div>
    </div>
  );
};

export default ChannelCreatedModal;
